import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
// Must import tgs-player entry point specifically — the default export only registers <lottie-player>
import '@lottiefiles/lottie-player/dist/tgs-player.esm.js';

interface StickerPlayerProps {
  url: string;
  mimeType?: string | null;
  size?: number;
  onClick?: () => void;
}

/**
 * Renders Telegram/WhatsApp stickers based on their format:
 * - Static (image/webp): <img> tag
 * - Animated TGS (application/x-tgsticker): <tgs-player> via blob URL to avoid CORS
 * - Video (video/webm): <video> with autoplay + loop
 */
export function StickerPlayer({ url, mimeType, size = 150, onClick }: StickerPlayerProps) {
  const isAnimatedTgs = mimeType === 'application/x-tgsticker';
  const isVideo = mimeType === 'video/webm';

  if (isAnimatedTgs) {
    return <TgsBlobPlayer url={url} size={size} onClick={onClick} />;
  }

  if (isVideo) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          cursor: onClick ? 'pointer' : 'default',
        }}
        onClick={onClick}
      >
        <video
          src={url}
          autoPlay
          loop
          muted
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>
    );
  }

  // Static sticker (WebP - Telegram & WhatsApp)
  return (
    <Box
      component="img"
      src={url}
      alt="sticker"
      sx={{
        display: 'block',
        width: size,
        height: size,
        objectFit: 'contain',
        cursor: onClick ? 'pointer' : 'default',
      }}
      loading="lazy"
      onClick={onClick}
    />
  );
}

/**
 * Fetches the TGS file from the presigned URL ourselves, creates a local
 * blob URL, and passes that to <tgs-player>. This avoids CORS issues since
 * <tgs-player> would otherwise make its own cross-origin XHR to the S3 URL.
 */
function TgsBlobPlayer({ url, size, onClick }: { url: string; size: number; onClick?: () => void }) {
  const ref = useRef<HTMLElement>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    async function fetchAndCreateBlob() {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        if (!cancelled) {
          setBlobUrl(objectUrl);
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch (err) {
        console.error('Failed to load TGS sticker:', err);
        if (!cancelled) setError(true);
      }
    }

    fetchAndCreateBlob();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  // Revoke the old blob URL when a new one is created
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (error) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '3rem',
        }}
      >
        🎨
      </Box>
    );
  }

  if (!blobUrl) {
    return <Box sx={{ width: size, height: size }} />;
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <tgs-player
        ref={ref}
        autoplay
        loop
        mode="normal"
        src={blobUrl}
        style={{ width: `${size}px`, height: `${size}px` }}
      />
    </Box>
  );
}

// Extend JSX types for the <tgs-player> web component
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'tgs-player': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          src?: string;
          autoplay?: boolean;
          loop?: boolean;
          mode?: string;
        },
        HTMLElement
      >;
    }
  }
}
